import './payment-surfaces.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, ConfirmDialog, Select, TextInput } from '../ui.tsx';
import { PaymentBadge } from './payment-badge.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import {
  applyCardFee,
  chargeFullyRefunded,
  formatBasisPointsPercent,
  refundableCentsForCharge,
} from '@shared/calculations/payments.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { MoneyInput } from '../money-input.tsx';
import { PdfDownloadButton } from '../pdf-download.tsx';
import type { Appointment, Payment } from '@shared/types/index.ts';

/**
 * The money panel for one visit: its price, its payment badge, the charge /
 * email-a-payment-link actions, recording a payment the office received
 * itself, and the ledger with per-charge refunds. Rendered inside the
 * appointment detail wherever `AppointmentActions` is.
 *
 * Recording works with or without a payment provider — cash and checks do not
 * need Stripe — so the panel never collapses to a "not configured" notice any
 * more; only the provider-backed buttons do.
 */
export function AppointmentPayments(props: { appointment: Appointment; onChanged: () => void }) {
  // One panel per visit. Everything it holds — an open refund form, a charge
  // or refund question waiting for its answer — belongs to the visit it was
  // opened on, so another visit gets a fresh panel rather than a question that
  // would act on the previous one's payment.
  return <VisitPaymentsPanel key={props.appointment.id} {...props} />;
}

function VisitPaymentsPanel({ appointment, onChanged }: { appointment: Appointment; onChanged: () => void }) {
  const { t } = useTranslation();
  /*
    The company's own payment-method labels, read by whoever is recording the
    money — a seeded, translated library like the room and task ones, so it
    follows the reader (see `localized-text.ts`).
  */
  const localized = useLocalizedContent();
  const { company, can, currencyCode: sessionCurrency } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  // The visit's price, in its own currency: a payment on it is taken in the
  // currency it was priced in, whatever is billed in today.
  const [recordAmount, setRecordAmount] = useState<number | null>(appointment.priceCents);
  const [recordMethodId, setRecordMethodId] = useState('');
  const [recordDate, setRecordDate] = useState('');
  const [recordNote, setRecordNote] = useState('');
  /** A tip received on top of the amount, offered only with tips switched on. */
  const [recordTip, setRecordTip] = useState<number | null>(null);
  // Which charge the refund form is open on, and what it is filled with. Held
  // here rather than gathered by `window.prompt`, which is what this panel used
  // to do: a browser is free to suppress that dialog outright — an in-app
  // webview, a page that already showed one, an "ignore further dialogs" tick —
  // and when it does, `prompt` answers null and the button that opened it looks
  // dead. Nothing about a refund should depend on a dialog the browser may
  // decline to draw, so the form is part of the page.
  const [refundFor, setRefundFor] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState('');
  // Both of these move money and neither can be taken back, so each asks first,
  // naming the amount — in a dialog on the page, for the reason above. A
  // submitted refund form (a phone keyboard's Go included) only raises the
  // question.
  // The charge question holds the figures it opened with — base, card total,
  // fee and card — and those are what it shows and sends. A ledger refresh
  // while it is open cannot change what is being confirmed; if the live
  // figures moved, the server refuses the snapshot and the office asks again.
  const [confirmingCharge, setConfirmingCharge] = useState<{
    amountCents: number;
    totalCents: number;
    feeCents: number;
    basisPoints: number;
    card: { last4: string; brand: string | null } | null;
  } | null>(null);
  const [confirmingRefund, setConfirmingRefund] = useState<{ payment: Payment; amountCents: number } | null>(null);

  // What a new payment on this visit is taken in: the currency it was priced
  // in, or the location's for a visit with no price yet — as the Worker does.
  const currency = appointment.priceCurrencyCode ?? sessionCurrency;
  // A ledger row is shown in the currency it was taken in, which is not
  // necessarily the company's current one.
  const rowMoney = (payment: Payment) => formatCents(payment.amountCents, payment.currencyCode, getDateLocale());
  // A charge that has not happened yet has no currency of its own, so the
  // previews below are in the currency the company bills in today.
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());
  const canWrite = can('payments:write');
  // Whether a text could go out for this company at all. Per-customer
  // eligibility is the server's call — see the button below.
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  const canRefund = can('payments:refund');

  const status = useQuery({ queryKey: ['payments-status'], queryFn: () => paymentApi.status() });
  const ledger = useQuery({
    queryKey: ['appointment-payments', appointment.id],
    queryFn: () => paymentApi.appointmentPayments(appointment.id),
  });
  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: () => paymentApi.listMethods(),
    enabled: canWrite,
  });

  const shared = {
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('appointmentPayments.actionFailed')),
  };
  function done(message: string) {
    setError(null);
    setNotice(message);
    void ledger.refetch();
    onChanged();
  }

  const charge = useMutation({
    // The amount and card total the question named, so a price, quote payment
    // or card fee that changed since this panel read them is refused rather
    // than charged unseen.
    mutationFn: (expected: {
      amountCents: number;
      totalCents: number;
      card: { last4: string; brand: string | null } | null;
    }) =>
      paymentApi.chargeAppointment(appointment.id, {
        expectedAmountCents: expected.amountCents,
        expectedTotalCents: expected.totalCents,
        // The card the question named: one replaced since is refused, not charged.
        ...(expected.card ? { expectedCardLast4: expected.card.last4 } : {}),
        ...(expected.card?.brand ? { expectedCardBrand: expected.card.brand } : {}),
      }),
    onSuccess: (payment) =>
      payment.status === 'succeeded'
        ? done(t('appointmentPayments.cardCharged'))
        : done(t('appointmentPayments.chargeStatus', { status: t(`payments.status.${payment.status}`).toLowerCase() })),
    onError: (caught: unknown) => {
      shared.onError(caught);
      // A refusal over a changed figure: the next question asks the new one.
      // The price comes from the visit itself, so its owner reads it again too.
      void ledger.refetch();
      onChanged();
    },
  });
  /**
   * The pay-by-link send, on whichever channel was pressed.
   *
   * One mutation rather than two: the request, the provider session and the
   * pending payment row are identical either way, and only the delivery
   * differs. Texting is a separate *button* rather than a pair of tickboxes
   * because the choice is made once, in the moment, and a form nobody fills in
   * is worse than two buttons that each say what they do.
   */
  const requestPayment = useMutation({
    mutationFn: (channel: 'email' | 'text') =>
      paymentApi.requestPayment(appointment.id, {
        sendEmail: channel === 'email',
        sendText: channel === 'text',
      }),
    onSuccess: (result, channel) =>
      done(
        channel === 'text'
          ? result.textFailed
            ? t('appointmentPayments.paymentLinkTextFailed', { url: result.url })
            : t(
                result.textDeferredUntilOpenHours
                  ? 'appointmentPayments.paymentLinkTextDeferred'
                  : 'appointmentPayments.paymentLinkTexted',
              )
          : t('appointmentPayments.paymentLinkEmailed'),
      ),
    ...shared,
  });
  const refund = useMutation({
    mutationFn: (input: { paymentId: string; amountCents?: number; reason?: string }) =>
      paymentApi.refund(input.paymentId, {
        ...(input.amountCents === undefined ? {} : { amountCents: input.amountCents }),
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: (payment) => {
      closeRefund();
      // What the LEDGER says, which is not always what this request set out
      // to do: a refund the processor would not confirm is parked `pending`
      // rather than claimed as money returned, and one the processor canceled
      // or declined — reachable here because `refundPayment` reads back a row
      // a settlement webhook got to first — is the opposite of issued. Saying
      // "Refund issued" for either is the false success this whole change
      // exists to end, and the person who pressed the button is often standing
      // in front of the customer when they read it.
      done(
        payment.status === 'pending'
          ? t('appointmentPayments.refundUnconfirmed', {
              reason: payment.failureReason ?? t('appointmentPayments.refundUnconfirmedAccepted'),
            })
          : payment.status === 'failed' || payment.status === 'cancelled'
            ? t('appointmentPayments.refundNotSent', { reason: payment.failureReason ?? '' })
            : t('appointmentPayments.refundIssued'),
      );
    },
    ...shared,
  });
  /**
   * The way out of a refund the processor never confirmed: ask it what
   * happened. Nothing here moves money — the answer either settles the row or
   * frees the amount to be refunded again.
   */
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const recheck = useMutation({
    mutationFn: (paymentId: string) => {
      setRecheckingId(paymentId);
      return paymentApi.recheckRefund(paymentId);
    },
    onSuccess: (payment) => {
      setRecheckingId(null);
      done(
        payment.status === 'succeeded'
          ? t('appointmentPayments.refundIssued')
          : payment.status === 'pending'
            ? // The provider sometimes says WHY it is unresolved — "this refund
              // needs confirming", which no amount of re-checking will clear by
              // itself. The lookup stores that reason precisely so somebody can
              // act on it, and the generic sentence threw it away.
              payment.failureReason
              ? t('appointmentPayments.refundStillUnconfirmedReason', { reason: payment.failureReason })
              : t('appointmentPayments.refundStillUnconfirmed')
            : t('appointmentPayments.refundNotSent', { reason: payment.failureReason ?? '' }),
      );
    },
    onError: (caught: unknown) => {
      setRecheckingId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('appointmentPayments.actionFailed'));
    },
  });
  const record = useMutation({
    mutationFn: () =>
      paymentApi.recordPayment({
        customerId: appointment.customerId,
        appointmentId: appointment.id,
        ...(recordAmount === null ? {} : { amountCents: recordAmount }),
        ...(recordTip !== null && recordTip > 0 ? { tipCents: recordTip } : {}),
        methodId: recordMethodId,
        ...(recordNote.trim() ? { description: recordNote.trim() } : {}),
        ...(recordDate ? { receivedOn: recordDate } : {}),
      }),
    onSuccess: () => {
      setRecording(false);
      setRecordNote('');
      setRecordDate('');
      setRecordTip(null);
      done(t('appointmentPayments.paymentRecorded'));
    },
    ...shared,
  });
  const [resendingId, setResendingId] = useState<string | null>(null);
  const resendReceipt = useMutation({
    mutationFn: (paymentId: string) => {
      setResendingId(paymentId);
      return paymentApi.resendReceipt(paymentId);
    },
    onSuccess: (result) => {
      setResendingId(null);
      done(t('appointmentPayments.receiptResent', { email: result.resentTo }));
    },
    onError: (caught: unknown) => {
      setResendingId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('appointmentPayments.actionFailed'));
    },
  });

  if (!can('payments:read')) return null;

  const configured = status.data?.configured === true;
  // Paid on the quote this visit booked, before it was booked: its invoice
  // counts it, and charging the visit takes only the rest.
  const paidOnQuote = ledger.data?.paidOnQuoteCents ?? 0;

  // The visit's own price currency, not the company's current one. Showing
  // "¥150" for a $150.00 visit would be a label the server then refuses to
  // charge — the operator has to see the money the visit actually carries.
  const priceLabel =
    appointment.priceCents !== null
      ? formatCents(appointment.priceCents, appointment.priceCurrencyCode ?? currency, getDateLocale())
      : t('appointmentPayments.priceNotSet');
  const billable = appointment.paymentStatus === 'not_billed' || appointment.paymentStatus === 'failed';

  // The two actions below do not charge the same fee, so they do not share a
  // preview. Charging the card on file is capped by what that card agreed to
  // when it was saved (the ledger knows this customer's rate); a payment link
  // is a fresh decision by the customer, so it carries the company's current
  // rate. They differ exactly when a stored card has been grandfathered.
  const price = Math.max(0, (appointment.priceCents ?? 0) - paidOnQuote);
  const cardFee = applyCardFee(price, { enabled: true, basisPoints: ledger.data?.cardFeeBasisPoints ?? 0 }, currency);
  const linkFee = applyCardFee(price, { enabled: true, basisPoints: status.data?.cardFeeBasisPoints ?? 0 }, currency);

  // An unanswered query is not a rate of zero. Until both land, the fees are
  // unknown, and nothing that charges a card may act on a guess.
  const feeKnown = ledger.data !== undefined && status.data !== undefined;
  /** The saved card a charge would take, from servers that say which. */
  const savedCard = ledger.data?.card ?? null;

  /** How much of this charge the server would still accept a refund for. */
  function refundableOn(paymentId: string): number {
    return refundableCentsForCharge(paymentId, ledger.data?.items ?? []);
  }

  function openRefund(payment: Payment) {
    setError(null);
    setNotice(null);
    setRefundFor(payment.id);
    // Prefilled with the whole remainder, which is the common refund; a partial
    // one is a matter of editing the figure down rather than typing it out.
    setRefundAmount(refundableOn(payment.id));
    setRefundReason('');
  }

  function closeRefund() {
    setRefundFor(null);
    setRefundAmount(null);
    setRefundReason('');
  }

  function submitRefund(event: FormEvent, payment: Payment) {
    event.preventDefault();
    const remaining = refundableOn(payment.id);
    if (refundAmount === null || refundAmount <= 0 || refundAmount > remaining) {
      // In the charge's own currency, like every other figure on its row: a
      // company that has since switched currencies still refunds in the money
      // it took.
      setError(
        t('appointmentPayments.refundInvalid', {
          amount: formatCents(remaining, payment.currencyCode, getDateLocale()),
        }),
      );
      return;
    }
    setConfirmingRefund({ payment, amountCents: refundAmount });
  }

  function issueRefund(payment: Payment, amountCents: number) {
    refund.mutate({
      paymentId: payment.id,
      // Always the figure the question named, never "everything left": the
      // server reads a missing amount as the whole cap, and a cap that grew
      // since this ledger was read (an earlier refund re-checked as never
      // sent) would refund more than was agreed to. A cap that shrank refuses
      // the figure instead, with a message naming what is left.
      amountCents,
      ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
    });
  }

  function submitRecord(event: FormEvent) {
    event.preventDefault();
    if (!recordMethodId) return;
    record.mutate();
  }

  function rowLabel(payment: Payment): string {
    if (payment.type === 'refund') return t('appointmentPayments.refundLabel');
    if (payment.method === 'manual') {
      return payment.manualMethodName ?? t('appointmentPayments.recordedLabel');
    }
    if (payment.method === 'tap_to_pay') return t('appointmentPayments.tapToPayLabel');
    return t('appointmentPayments.chargeLabel');
  }

  const activeMethods = methods.data?.items ?? [];

  return (
    <div className="payment-leaf mt-4 border-t border-[color:var(--color-line)] pt-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {t('appointmentPayments.paymentLabel')}{' '}
          <span className="font-normal text-[color:var(--color-muted)]">({priceLabel})</span>
        </h3>
        <PaymentBadge status={appointment.paymentStatus} />
      </div>
      {paidOnQuote > 0 ? (
        <p className="mb-2 text-sm text-[color:var(--color-muted)]">
          {t('appointmentPayments.paidOnQuote', { amount: money(paidOnQuote) })}
        </p>
      ) : null}

      {error ? (
        <div className="mb-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-2">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      {configured && feeKnown && (cardFee.feeCents > 0 || linkFee.feeCents > 0) ? (
        <div className="mb-2 text-sm text-[color:var(--color-muted)]">
          {cardFee.basisPoints === linkFee.basisPoints ? (
            <p>
              {t('appointmentPayments.cardFeeNotice', {
                percent: formatBasisPointsPercent(cardFee.basisPoints),
                fee: money(cardFee.feeCents),
                total: money(cardFee.totalCents),
              })}
            </p>
          ) : (
            // Split, because the office is about to choose between two
            // buttons that will take different amounts.
            <>
              <p>
                {cardFee.feeCents > 0
                  ? t('appointmentPayments.cardOnFileFeeNotice', {
                      percent: formatBasisPointsPercent(cardFee.basisPoints),
                      total: money(cardFee.totalCents),
                    })
                  : t('appointmentPayments.cardOnFileNoFeeNotice', { total: money(cardFee.totalCents) })}
              </p>
              <p>
                {t('appointmentPayments.linkFeeNotice', {
                  percent: formatBasisPointsPercent(linkFee.basisPoints),
                  total: money(linkFee.totalCents),
                })}
              </p>
            </>
          )}
        </div>
      ) : null}

      {canWrite && appointment.status !== 'cancelled' ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {configured && billable ? (
            <>
              {/* Held until the ledger answers, because that is what carries
                  the fee: charging is irreversible, and a button pressed a
                  moment early would take an amount nobody was shown. The same
                  answer says whether there is a card to charge — offered on a
                  connected processor alone, this was a button that could only
                  return "no card on file" for every customer who has not saved
                  one. The payment-link buttons beside it stay either way: a
                  link is how a customer WITHOUT a card pays. */}
              {ledger.data?.cardOnFile ? (
                <Button
                  busy={charge.isPending}
                  // Nothing to charge — no price, or paid in full on its quote —
                  // is no question to ask: a charge always names its figure.
                  disabled={!feeKnown || price <= 0}
                  onClick={() =>
                    setConfirmingCharge({
                      amountCents: price,
                      totalCents: cardFee.totalCents,
                      feeCents: cardFee.feeCents,
                      basisPoints: cardFee.basisPoints,
                      card: savedCard?.last4 ? { last4: savedCard.last4, brand: savedCard.brand } : null,
                    })
                  }
                >
                  {t('appointmentPayments.chargeCard')}
                </Button>
              ) : null}
              <Button
                busy={requestPayment.isPending}
                disabled={!feeKnown}
                onClick={() => requestPayment.mutate('email')}
              >
                {t('appointmentPayments.emailPaymentLink')}
              </Button>
              {/* Offered only where the company can text at all. Whether *this*
                  customer may be — consent, the line, the hour — is the
                  server's answer, and it comes back as a readable error rather
                  than being second-guessed here. */}
              {companyCanText ? (
                <Button
                  busy={requestPayment.isPending}
                  disabled={!feeKnown}
                  onClick={() => requestPayment.mutate('text')}
                >
                  {t('appointmentPayments.textPaymentLink')}
                </Button>
              ) : null}
            </>
          ) : null}
          <Button
            variant="ghost"
            onClick={() => {
              // Opened, the form starts at what is left: the price, less what
              // was paid on the quote. A price in an old currency stays empty.
              if (!recording && paidOnQuote > 0 && recordAmount === appointment.priceCents) {
                setRecordAmount(recordAmount === null ? null : Math.max(0, recordAmount - paidOnQuote));
              }
              setRecording((value) => !value);
            }}
          >
            {recording ? t('appointmentPayments.recordCancel') : t('appointmentPayments.recordPayment')}
          </Button>
        </div>
      ) : null}
      {configured && canWrite && billable && appointment.status !== 'cancelled' && !feeKnown ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">
          {ledger.isError ? t('appointmentPayments.feeUnknown') : t('appointmentPayments.loadingFee')}
        </p>
      ) : null}
      {status.data && !configured && canWrite ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('appointmentPayments.notConfiguredHint')}</p>
      ) : null}

      {recording && canWrite ? (
        <form
          onSubmit={submitRecord}
          className="mb-3 grid gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-2"
        >
          <MoneyInput
            // With a tip field beside it, this field must say which half it
            // is — "amount received" alone invites typing the handed-over
            // total here AND the tip below, over-recording by the tip.
            label={
              status.data?.tips.enabled
                ? t('appointmentPayments.recordAmountBeforeTipLabel')
                : t('appointmentPayments.recordAmountLabel')
            }
            valueCents={recordAmount}
            onChangeCents={setRecordAmount}
            currencyCode={currency}
          />
          <Select
            label={t('appointmentPayments.recordMethodLabel')}
            value={recordMethodId}
            onChange={(event) => setRecordMethodId(event.target.value)}
            options={[
              { value: '', label: t('appointmentPayments.recordMethodPlaceholder') },
              ...activeMethods.map((method) => ({
                value: method.id,
                label: localized.text(method.name, method.nameTranslations),
              })),
            ]}
          />
          {/* Only where this location has tips switched on; the server refuses
              a tip anywhere else, and a field that only ever errors is worse
              than no field. */}
          {status.data?.tips.enabled ? (
            <MoneyInput
              label={t('appointmentPayments.recordTipLabel')}
              hint={t('appointmentPayments.recordTipHint')}
              valueCents={recordTip}
              onChangeCents={setRecordTip}
              currencyCode={currency}
            />
          ) : null}
          <TextInput
            label={t('appointmentPayments.recordDateLabel')}
            type="date"
            value={recordDate}
            onChange={(event) => setRecordDate(event.target.value)}
          />
          <TextInput
            label={t('appointmentPayments.recordNoteLabel')}
            value={recordNote}
            onChange={(event) => setRecordNote(event.target.value)}
            placeholder={t('appointmentPayments.recordNotePlaceholder')}
            maxLength={200}
          />
          <div className="sm:col-span-2">
            <Button
              type="submit"
              variant="primary"
              busy={record.isPending}
              disabled={!recordMethodId || recordAmount === null || recordAmount <= 0}
            >
              {t('appointmentPayments.recordSubmit')}
            </Button>
          </div>
        </form>
      ) : null}

      {ledger.data && ledger.data.items.length > 0 ? (
        <ul className="divide-y divide-[color:var(--color-line)] text-sm">
          {ledger.data.items.map((payment) => {
            const remaining =
              canRefund && payment.type === 'charge' && payment.status === 'succeeded' ? refundableOn(payment.id) : 0;
            const open = refundFor === payment.id;
            return (
              <li key={payment.id} className="py-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {rowLabel(payment)} · <span className="tabular font-medium">{rowMoney(payment)}</span>
                    {payment.cardFeeCents > 0 ? (
                      <span className="ml-2 text-[color:var(--color-muted)]">
                        {/* In the row's own currency, like the amount beside
                            it — a fee taken in euros is not a dollar figure. */}
                        {t('appointmentPayments.cardFeeIncluded', {
                          fee: formatCents(payment.cardFeeCents, payment.currencyCode, getDateLocale()),
                        })}
                      </span>
                    ) : null}
                    {payment.tipCents > 0 ? (
                      <span className="ml-2 text-[color:var(--color-muted)]">
                        {t('appointmentPayments.tipIncluded', {
                          tip: formatCents(payment.tipCents, payment.currencyCode, getDateLocale()),
                        })}
                      </span>
                    ) : null}
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {t(`payments.status.${payment.status}`)}
                      {payment.failureReason ? ` — ${payment.failureReason}` : ''}
                    </span>
                  </span>
                  {/* A charge whose remainder is gone keeps its row but loses
                      the button: an operator pressing "Refund" on something
                      already refunded in full only ever gets a 409 back. The
                      same goes for one whose account is no longer connected —
                      it says so instead, since the answer is a settings change
                      rather than anything to do with this visit. The button
                      also stands down while its own form is open — the form has
                      a Cancel, and two controls a thumb apart both reading
                      "Cancel" is a coin toss, not a choice. */}
                  <div className="flex flex-wrap items-center gap-2">
                    {payment.type === 'charge' && payment.status === 'succeeded' ? (
                      <>
                        <PdfDownloadButton url={paymentApi.receiptUrl(payment.id)} variant="link">
                          {t('appointmentPayments.downloadReceipt')}
                        </PdfDownloadButton>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            busy={resendingId === payment.id}
                            onClick={() => resendReceipt.mutate(payment.id)}
                          >
                            {t('appointmentPayments.resendReceipt')}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {/* A refund nobody has heard back about: the one row the
                        office can do something about, and the button that does
                        it. */}
                    {payment.type === 'refund' && payment.status === 'pending' && canRefund ? (
                      <Button
                        variant="ghost"
                        busy={recheckingId === payment.id}
                        onClick={() => recheck.mutate(payment.id)}
                      >
                        {t('appointmentPayments.refundRecheck')}
                      </Button>
                    ) : null}
                    {remaining > 0 && !payment.providerAccountConnected ? (
                      <span className="text-[color:var(--color-muted)]">
                        {t('appointmentPayments.accountDisconnected')}
                      </span>
                    ) : remaining > 0 && !open ? (
                      <Button variant="ghost" onClick={() => openRefund(payment)}>
                        {t('appointmentPayments.refund')}
                      </Button>
                    ) : payment.type === 'charge' &&
                      payment.status === 'succeeded' &&
                      canRefund &&
                      chargeFullyRefunded(payment.id, ledger.data?.items ?? []) ? (
                      /* Said of money that has actually gone back, not of a
                         charge with nothing left to offer. A refund still in
                         flight empties the cap without refunding anything, and
                         this row would otherwise announce a full refund
                         directly above the unconfirmed refund it is waiting
                         on. While that is unresolved the row says neither —
                         the refund's own line, and its re-check, are the true
                         account of where the money is. */
                      <span className="text-[color:var(--color-muted)]">{t('appointmentPayments.fullyRefunded')}</span>
                    ) : null}
                  </div>
                </div>

                {open ? (
                  <form
                    onSubmit={(event) => submitRefund(event, payment)}
                    className="mt-2 grid gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-2"
                  >
                    <MoneyInput
                      label={t('appointmentPayments.refundAmountLabel')}
                      hint={t('appointmentPayments.refundAmountHint', {
                        amount: formatCents(remaining, payment.currencyCode, getDateLocale()),
                      })}
                      valueCents={refundAmount}
                      onChangeCents={setRefundAmount}
                      currencyCode={payment.currencyCode}
                    />
                    <TextInput
                      label={t('appointmentPayments.refundReasonLabel')}
                      value={refundReason}
                      onChange={(event) => setRefundReason(event.target.value)}
                      placeholder={t('appointmentPayments.refundReasonPlaceholder')}
                      maxLength={200}
                    />
                    <div className="flex flex-wrap gap-2 sm:col-span-2">
                      <Button
                        type="submit"
                        variant="primary"
                        busy={refund.isPending}
                        disabled={refundAmount === null || refundAmount <= 0 || refundAmount > remaining}
                      >
                        {t('appointmentPayments.refundSubmit')}
                      </Button>
                      <Button variant="ghost" onClick={closeRefund}>
                        {t('appointmentPayments.refundCancel')}
                      </Button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {confirmingCharge ? (
        <ConfirmDialog
          title={
            confirmingCharge.card
              ? t('appointmentPayments.confirmChargeCard', {
                  amount: money(confirmingCharge.totalCents),
                  last4: confirmingCharge.card.last4,
                })
              : t('appointmentPayments.confirmCharge', { amount: money(confirmingCharge.totalCents) })
          }
          confirmLabel={t('appointmentPayments.chargeCard')}
          initialFocus="cancel"
          onConfirm={() => {
            const confirmed = confirmingCharge;
            setConfirmingCharge(null);
            // Only a figure can be confirmed; without one the server would
            // charge whatever the visit comes to by then.
            if (confirmed.amountCents > 0) charge.mutate(confirmed);
          }}
          onCancel={() => setConfirmingCharge(null)}
        >
          {confirmingCharge.feeCents > 0 ? (
            <p className="text-sm text-[color:var(--color-muted)]">
              {t('appointmentPayments.cardOnFileFeeNotice', {
                percent: formatBasisPointsPercent(confirmingCharge.basisPoints),
                total: money(confirmingCharge.totalCents),
              })}
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}
      {confirmingRefund ? (
        <ConfirmDialog
          title={t(
            confirmingRefund.payment.method === 'manual'
              ? 'singleJob.confirmRecordedRefund'
              : 'singleJob.confirmRefund',
            {
              amount: formatCents(confirmingRefund.amountCents, confirmingRefund.payment.currencyCode, getDateLocale()),
            },
          )}
          confirmLabel={t('appointmentPayments.refundSubmit')}
          initialFocus="cancel"
          onConfirm={() => {
            setConfirmingRefund(null);
            issueRefund(confirmingRefund.payment, confirmingRefund.amountCents);
          }}
          onCancel={() => setConfirmingRefund(null)}
        />
      ) : null}
    </div>
  );
}
